import { useState, useEffect } from "react";
import { Table, CombinedTable } from "@/types/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface CombineTablesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTable: Table | null;
  allTables: Table[];
  onCreateCombination: (tableIds: string[]) => void;
}

export function CombineTablesSheet({
  open,
  onOpenChange,
  currentTable,
  allTables,
  onCreateCombination,
}: CombineTablesSheetProps) {
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);

  useEffect(() => {
    if (currentTable && open) {
      setSelectedTableIds([currentTable.id]);
    } else {
      setSelectedTableIds([]);
    }
  }, [currentTable, open]);

  const availableTables = allTables.filter(
    (t) => t.isActive && (!currentTable || t.id !== currentTable.id)
  );

  const totalCapacity = allTables
    .filter((t) => selectedTableIds.includes(t.id))
    .reduce((sum, t) => sum + t.capacity, 0);

  const selectedTables = allTables.filter((t) => selectedTableIds.includes(t.id));

  const toggleTable = (tableId: string) => {
    setSelectedTableIds((prev) =>
      prev.includes(tableId)
        ? prev.filter((id) => id !== tableId)
        : [...prev, tableId]
    );
  };

  const handleCreate = () => {
    if (selectedTableIds.length >= 2) {
      onCreateCombination(selectedTableIds);
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Créer une combinaison de tables</SheetTitle>
          <SheetDescription>
            Sélectionnez les tables à combiner pour créer une nouvelle configuration
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Table actuelle */}
          {currentTable && (
            <div className="p-4 border rounded-lg bg-muted/30">
              <Label className="text-sm font-medium mb-2 block">Table sélectionnée</Label>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{currentTable.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-muted-foreground flex items-center">
                      <Users className="w-3 h-3 mr-1" />
                      {currentTable.capacity} pers.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Liste des tables disponibles */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Tables disponibles à combiner</Label>
            {availableTables.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 border rounded-lg text-center">
                Aucune table active disponible
              </p>
            ) : (
              <div className="space-y-2">
                {availableTables.map((table) => (
                  <div
                    key={table.id}
                    className={`flex items-center justify-between p-3 border rounded-lg transition-colors cursor-pointer hover:bg-muted/50 ${
                      selectedTableIds.includes(table.id) ? "bg-muted border-primary" : ""
                    }`}
                    onClick={() => toggleTable(table.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedTableIds.includes(table.id)}
                        onCheckedChange={() => toggleTable(table.id)}
                      />
                      <div>
                        <p className="font-medium text-sm">{table.name}</p>
                        <span className="text-xs text-muted-foreground flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          {table.capacity} pers.
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Prévisualisation */}
          {selectedTableIds.length > 0 && (
            <div className="p-4 border rounded-lg bg-primary/5 border-primary/20">
              <Label className="text-sm font-medium mb-2 block">Prévisualisation</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Tables sélectionnées:</span>
                  <span className="font-medium">{selectedTableIds.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Capacité totale:</span>
                  <span className="font-bold text-lg text-primary">{totalCapacity} places</span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button
              className="flex-1"
              onClick={handleCreate}
              disabled={selectedTableIds.length < 2}
            >
              Créer cette combinaison
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
